import {Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {NgxChartsModule, Color, ScaleType} from '@swimlane/ngx-charts';
import {Observable, forkJoin} from 'rxjs';
import {TopBuyerStats, TopBuyerTrendStats, TrendGranularity} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

interface ChartDataPoint {
  name: string;
  value: number;
}

@Component({
  selector: 'app-winols-analytics',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './winols-analytics.component.html',
  styleUrls: ['./winols-analytics.component.css']
})
export class WinolsAnalyticsComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mode$: Observable<PeriodMode> = toObservable(this.periodService.mode);

  granularity = signal<TrendGranularity>('DAY');
  isLoading = signal<boolean>(true);

  topBuyers: TopBuyerStats[] = [];
  chartData: ChartDataPoint[] = [];
  totalCredits = 0;
  totalOrders = 0;

  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#7770CD', '#8A83D6', '#9D97DF', '#B0ABE8', '#C3BFF1']
  };

  ngOnInit(): void {
    this.mode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadAll();
    });
  }

  onGranularityChange(g: TrendGranularity): void {
    this.granularity.set(g);
    this.loadAll();
  }

  private loadAll(): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    this.isLoading.set(true);
    forkJoin({
      trend: this.statsService.getTopBuyersTrend(start, end, this.granularity()),
      buyers: this.statsService.getTopBuyers(start, end)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({trend, buyers}) => {
          this.topBuyers = buyers;
          this.buildChartData(trend);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  private buildChartData(trendData: TopBuyerTrendStats[]): void {
    const periodMap = new Map<string, { revenue: number; orders: number }>();

    for (const item of trendData) {
      const existing = periodMap.get(item.period) || {revenue: 0, orders: 0};
      existing.revenue += item.totalRevenue;
      existing.orders += item.totalOrders;
      periodMap.set(item.period, existing);
    }

    this.chartData = Array.from(periodMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, data]) => ({name: period, value: data.revenue}));

    this.totalCredits = Array.from(periodMap.values()).reduce((sum, d) => sum + d.revenue, 0);
    this.totalOrders = Array.from(periodMap.values()).reduce((sum, d) => sum + d.orders, 0);
  }
}
